import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { debugLog } from '@Utils/debugLog';
import { FormationSchema, ValidationResult } from '@Global/types/formations';
import { getFlexibleFormation } from '@Global/utils/formations/flexibleFormations';
import { Card } from '@Apollo/types';

/**
 * Extended card type with optional score
 */
export type ExtendedCard = Card & { cardScore?: number };

/**
 * Extended team structure для поддержки множественных позиций
 */
export type ExtendedTeam = {
  // Метаданные команды
  id?: string;
  gameId?: string;
  captain?: ExtendedCard | null;
  hash?: string;
  score?: number;
  place?: number;
} & {
  // Позиции игроков с динамическими ключами (goalkeeper-0, back-0, back-1, etc.)
  [positionKey: string]: ExtendedCard | string | number | null | undefined;
};

/**
 * Team state
 */
export type TeamState = {
  // Основное состояние команды
  team: ExtendedTeam;
  currentSessionInitialTeam: ExtendedTeam | null;

  // Формация и настройки
  formation: FormationSchema;
  availableFormations: FormationSchema[];
  teamSize: number;

  // Капитан и его изменения
  captain: ExtendedCard | null;
  captainChanged: boolean;

  // Валидация
  validation: ValidationResult;

  // UI состояние
  isLoading: boolean;
  isDirty: boolean; // есть ли несохраненные изменения

  // Кеш
  cachedTeams: Record<string, ExtendedTeam>; // gameId -> team

  // Отслеживание инициализации для предотвращения повторных загрузок
  lastInitializedKey: string | null; // Ключ последней инициализации (gameId-teamId)
};

/**
 * Начальное состояние
 */
const initialState: TeamState = {
  team: {},
  currentSessionInitialTeam: null,
  formation: getFlexibleFormation(5),
  availableFormations: [getFlexibleFormation(5)],
  teamSize: 5,
  captain: null,
  captainChanged: false,
  validation: {
    valid: false,
    errors: [],
    warnings: [],
  },
  isLoading: false,
  isDirty: false,
  cachedTeams: {},
  lastInitializedKey: null,
};

/**
 * Helper function для обновления кеша команды
 */
const updateTeamCache = (state: TeamState) => {
  if (state.team.gameId && !state.team.id) {
    // Immer автоматически создаст immutable копию
    state.cachedTeams[state.team.gameId] = state.team;
  }
};

/**
 * Team  slice
 */
export const teamSlice = createSlice({
  name: 'team',
  initialState,
  reducers: {
    /**
     * Инициализация команды для турнира
     */
    initializeTeam: (
      state,
      action: PayloadAction<{
        gameId: string;
        teamSize: number;
        existingTeam?: ExtendedTeam;
        hasReservePosition?: boolean;
      }>,
    ) => {
      const { gameId, teamSize, existingTeam, hasReservePosition } = action.payload;

      debugLog('🔄 teamSlice', 'initializeTeam called:', {
        gameId,
        teamSize,
        hasExistingTeam: !!existingTeam,
        existingTeamId: existingTeam?.id,
        hasCachedTeam: !!state.cachedTeams[gameId],
        cachedTeamId: state.cachedTeams[gameId]?.id,
        cachedTeamPlayers: state.cachedTeams[gameId]
          ? Object.keys(state.cachedTeams[gameId]).filter((k) => k.includes('-')).length
          : 0,
        currentLastInitializedKey: state.lastInitializedKey,
      });

      // Используем флексибльную формацию с учетом hasReservePosition
      const selectedFormation = getFlexibleFormation(teamSize, hasReservePosition);

      state.teamSize = teamSize;
      state.formation = selectedFormation;
      state.availableFormations = [selectedFormation]; // Используем только выбранную флексибльную формацию

      // Инициализируем команду (Immer позволяет прямое мутирование)
      if (existingTeam) {
        debugLog('✅ teamSlice', 'Using existing team from props:', {
          teamId: existingTeam.id,
          playerCount: Object.keys(existingTeam).filter((k) => k.includes('-')).length,
        });
        // Можем напрямую присваивать объекты - Immer создаст immutable копию
        state.team = existingTeam;
        state.team.gameId = gameId; // Прямое мутирование
        // ВАЖНО: Создаем глубокую копию для initialTeam чтобы избежать проблем с Immer proxy
        state.currentSessionInitialTeam = JSON.parse(JSON.stringify(existingTeam));
        state.captain = existingTeam.captain || null;
      } else {
        // Попробуем загрузить из кеша
        const cachedTeam = state.cachedTeams[gameId];
        if (cachedTeam) {
          debugLog('⚠️ teamSlice', 'Loading team from CACHE (this might be the issue!):', {
            gameId,
            cachedTeamId: cachedTeam.id,
            cachedTeamPlayers: Object.keys(cachedTeam).filter((k) => k.includes('-')).length,
          });
          state.team = cachedTeam;
          state.team.gameId = gameId; // Прямое мутирование
          state.captain = cachedTeam.captain || null;
        } else {
          debugLog('🆕 teamSlice', 'Creating NEW empty team for gameId:', gameId);
          // Создаем пустую команду
          state.team = { gameId };
          state.captain = null;
        }
        state.currentSessionInitialTeam = null;
      }

      state.captainChanged = false;
      // ВАЖНО: isDirty должен быть false для существующих команд с сервера!
      // isDirty = true только если есть изменения относительно initialTeam
      const hasCachedTeam = !existingTeam && state.cachedTeams[gameId];
      state.isDirty = !!hasCachedTeam; // true только для команд из кеша (черновиков)
      state.isLoading = false;

      // Сохраняем ключ инициализации для предотвращения повторных загрузок
      // Формат: gameId-teamId или gameId-new для новых команд
      const teamIdPart = existingTeam?.id || 'new';
      state.lastInitializedKey = `${gameId}-${teamIdPart}`;
    },

    /**
     * Добавление игрока на позицию
     */
    addPlayerToPosition: (
      state,
      action: PayloadAction<{
        positionKey: string;
        card: ExtendedCard;
      }>,
    ) => {
      const { positionKey, card } = action.payload;

      state.team[positionKey] = card;
      state.isDirty = true;

      // Автоматически назначаем капитана, если его нет
      if (!state.captain && positionKey !== 'reserve-0') {
        state.captain = card;
        state.team.captain = card;
      }

      // Для расширенных команд формация остается флексибльной - не переключаем автоматически

      // Сохраняем в кеш
      updateTeamCache(state);
    },

    /**
     * Удаление игрока с позиции
     */
    removePlayerFromPosition: (
      state,
      action: PayloadAction<{
        positionKey: string;
      }>,
    ) => {
      const { positionKey } = action.payload;
      const removedCard = state.team[positionKey];

      state.team[positionKey] = undefined;
      state.isDirty = true;

      // Если удаляем капитана, сбрасываем его
      if (
        removedCard &&
        typeof removedCard === 'object' &&
        'id' in removedCard &&
        state.captain?.id === removedCard.id
      ) {
        state.captain = null;
        state.team.captain = null;
        state.captainChanged = true;
      }

      // Для расширенных команд формация остается флексибльной - не переключаем автоматически

      // Сохраняем в кеш
      updateTeamCache(state);
    },

    /**
     * Установка капитана
     */
    setCaptain: (
      state,
      action: PayloadAction<{
        card: ExtendedCard | null;
      }>,
    ) => {
      const { card } = action.payload;

      const previousCaptain = state.captain;
      state.captain = card;
      state.team.captain = card;

      // Отмечаем изменение капитана, если он был установлен ранее
      if (previousCaptain) {
        state.captainChanged = true;
      }

      state.isDirty = true;

      // Сохраняем в кеш
      updateTeamCache(state);
    },

    /**
     * Смена формации
     */
    // changeFormation больше не используется в флексибльной архитектуре
    // Формация определяется автоматически на основе teamSize

    /**
     * Обновление команды целиком
     */
    updateTeam: (
      state,
      action: PayloadAction<{
        team: Partial<ExtendedTeam>;
        isInitial?: boolean;
      }>,
    ) => {
      const { team, isInitial } = action.payload;

      state.team = { ...state.team, ...team };

      if (isInitial) {
        state.currentSessionInitialTeam = state.team; // Immer создаст копию автоматически
      }

      if (team.captain !== undefined) {
        state.captain = team.captain;
      }

      state.isDirty = !isInitial;
    },

    /**
     * Сброс команды
     */
    resetTeam: (
      state,
      action: PayloadAction<{
        gameId?: string;
        shouldEraseCachedTeam?: boolean;
      }>,
    ) => {
      const { gameId, shouldEraseCachedTeam } = action.payload;

      debugLog('🔄 teamSlice', 'resetTeam called:', {
        gameId,
        shouldEraseCachedTeam,
        hasCachedTeam: gameId ? !!state.cachedTeams[gameId] : false,
        cachedTeamId: gameId && state.cachedTeams[gameId] ? state.cachedTeams[gameId].id : null,
        currentTeamId: state.team?.id,
      });

      state.team = gameId ? { gameId } : {};
      state.currentSessionInitialTeam = null;
      state.captain = null;
      state.captainChanged = false;
      state.isDirty = false;
      // Сбрасываем ключ инициализации при сбросе команды
      state.lastInitializedKey = null;

      if (shouldEraseCachedTeam && gameId) {
        debugLog('🗑️ teamSlice', 'Erasing cached team for gameId:', gameId);
        delete state.cachedTeams[gameId];
      } else if (gameId && state.cachedTeams[gameId]) {
        debugLog('⚠️ teamSlice', 'Cache NOT erased, team remains in cache:', {
          gameId,
          cachedTeamId: state.cachedTeams[gameId].id,
        });
      }
    },

    /**
     * Установка результатов валидации
     */
    setValidation: (state, action: PayloadAction<ValidationResult>) => {
      state.validation = action.payload;
    },

    /**
     * Установка состояния загрузки
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    /**
     * Сброс флага изменения капитана
     */
    resetCaptainChanged: (state) => {
      state.captainChanged = false;
    },

    /**
     * Сохранение команды в кеш
     */
    cacheTeam: (
      state,
      action: PayloadAction<{
        gameId: string;
        team: ExtendedTeam;
      }>,
    ) => {
      const { gameId, team } = action.payload;
      // Immer автоматически создаст immutable копию
      state.cachedTeams[gameId] = team;
    },

    /**
     * Очистка кеша для игры
     */
    clearGameCache: (state, action: PayloadAction<{ gameId: string }>) => {
      const { gameId } = action.payload;
      delete state.cachedTeams[gameId];
    },

    /**
     * Обновление хеша команды (после успешного сохранения)
     */
    updateTeamHash: (
      state,
      action: PayloadAction<{
        hash: string;
        teamId?: string;
      }>,
    ) => {
      const { hash, teamId } = action.payload;

      debugLog('✅ teamSlice', 'updateTeamHash - team saved successfully:', {
        hash,
        teamId,
        gameId: state.team.gameId,
        willClearCache: !!state.team.gameId,
      });

      state.team.hash = hash;
      if (teamId) {
        state.team.id = teamId;
      }

      state.isDirty = false;
      state.captainChanged = false;

      // Обновляем initialTeam после успешного коммита
      // чтобы кнопка reset не показывалась когда нет несохраненных изменений
      state.currentSessionInitialTeam = { ...state.team };

      // Очищаем кеш после успешного сохранения
      if (state.team.gameId) {
        debugLog('🗑️ teamSlice', 'Clearing cache after successful save for gameId:', state.team.gameId);
        delete state.cachedTeams[state.team.gameId];
      } else {
        debugLog('⚠️ teamSlice', 'Cannot clear cache - no gameId in team!');
      }
    },
  },
});

// Экспорт действий
export const {
  initializeTeam,
  addPlayerToPosition,
  removePlayerFromPosition,
  setCaptain,
  // changeFormation - удалено, т.к. не используется в флексибльной архитектуре
  updateTeam,
  resetTeam,
  setValidation,
  setLoading,
  resetCaptainChanged,
  cacheTeam,
  clearGameCache,
  updateTeamHash,
} = teamSlice.actions;

// Экспорт редьюсера
export const teamReducer = teamSlice.reducer;
